// useNotifications.tsx - Pure toast lifecycle management

import { useEffect, useRef } from "react";
import { LazyMotion, domAnimation, m, AnimatePresence } from "framer-motion";
import toast from "react-hot-toast";
import { Toast } from "../components/Toast";
import { Notifications } from "../types/Notifications";

type UseNotificationsProps = {
    notifications: Notifications;
};

export const useNotifications = ({ notifications }: UseNotificationsProps) => {
    const { toasts } = notifications;

    const shownToastIdsRef = useRef(new Set<string>());
    const activeToastIdsRef = useRef(new Set<string>());

    useEffect(() => {
        // Cleanup toast IDs when the hook unmounts
        const currentActiveIds = activeToastIdsRef.current;
        return () => {
            currentActiveIds.forEach(id => toast.dismiss(id));
        };
    }, []);

    useEffect(() => {
        const currentToastIds = new Set(toasts?.map(t => t.id) || []);
        const activeIds = activeToastIdsRef.current;

        // Dismiss toasts that are no longer in the array
        activeIds.forEach(activeId => {
            if (!currentToastIds.has(activeId)) {
                toast.dismiss(activeId);
                activeIds.delete(activeId);
            }
        });

        // Process current toasts
        if (toasts?.length) {
            toasts.forEach(toastItem => {
                if (toastItem.visible) {
                    // Check if this toast has NOT been shown yet in this session
                    if (!shownToastIdsRef.current.has(toastItem.id)) {
                        // Mark as shown for this session if the duration is not Infinity
                        if (toastItem.duration !== Infinity) {
                            shownToastIdsRef.current.add(toastItem.id);
                        }

                        // Track as active
                        activeToastIdsRef.current.add(toastItem.id);

                        toast.custom(
                            t => {
                                return (
                                    <LazyMotion features={domAnimation}>
                                        <AnimatePresence>
                                            {t.visible && (
                                                <m.div
                                                    key={toastItem.id}
                                                    initial={{ opacity: 0, y: -20 }}
                                                    animate={{ opacity: 1, y: 0, transition: { delay: 1 } }}
                                                    exit={{ opacity: 0, y: -20 }}
                                                    transition={{ duration: 0.25 }}
                                                >
                                                    <Toast
                                                        isCloseDisabled={toastItem.isCloseDisabled}
                                                        closeToast={() => {
                                                            if (toastItem.onClose) {
                                                                toastItem.onClose();
                                                            }
                                                            toast.dismiss(toastItem.id);
                                                            activeToastIdsRef.current.delete(toastItem.id); // Remove from active tracking
                                                        }}
                                                    >
                                                        {toastItem.message}
                                                    </Toast>
                                                </m.div>
                                            )}
                                        </AnimatePresence>
                                    </LazyMotion>
                                );
                            },
                            {
                                id: toastItem.id,
                                duration: toastItem.duration,
                                position: toastItem.position
                            }
                        );
                    } else {
                        toast.dismiss(toastItem.id);
                        activeToastIdsRef.current.delete(toastItem.id);
                    }
                }
            });
        }
    }, [toasts]);
};
